import React, { useState } from "react";
import { Box, Button, Card, Icon, Alert, Badge, Dropdown, Spinner, Grid } from "~/components-shared";
import styled from "styled-components";
import mixpanel from "mixpanel-browser";
import { useTranslation } from "react-i18next";
import { AuthoritiesEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import toastsActionCreators from "~/store/toasts/actions";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
const StyledBox = styled(Box)`
  position: absolute;
  top: 5px;
  right: 5px;
`;
const StyledTopicButton = styled(Button)`
  & span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const AchievementItemCard = _ref => {
  let {
    showDismissed,
    item,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const [itemState, setItemState] = useState(item);
  const dismissStatus = useSelector(insightsSelectors.getDismissStatusByLineItem(item.id));
  const deleteStatus = useSelector(insightsSelectors.getDeleteStatusByLineItem(item.id));
  const dismissError = useSelector(insightsSelectors.getDismissErrorByLineItem(item.id));
  const deleteError = useSelector(insightsSelectors.getDeleteErrorByLineItem(item.id));
  const error = dismissError || deleteError;
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  if (!itemState || !showDismissed && itemState.isDismissed) {
    return null;
  }
  const handleToggleDismiss = () => {
    if (itemState.isDismissed) {
      dispatch(insightsActionCreators.restoreLineItemStarted(item.id, _ref2 => {
        let {
          data
        } = _ref2;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("AchievementItemCard.successfullyRestored", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          mixpanel.track(`Insights Highlights – Restore`, {
            lineItemId: item.id
          });
        }
      }));
    } else {
      dispatch(insightsActionCreators.dismissLineItemStarted(item.id, _ref3 => {
        let {
          data
        } = _ref3;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("AchievementItemCard.successfullyDismissed", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          mixpanel.track(`Insights Highlights – Dismiss`, {
            lineItemId: item.id
          });
        }
      }));
    }
  };
  const handleDelete = () => {
    dispatch(insightsActionCreators.deleteLineItemStarted(item.id, _ref4 => {
      let {
        data
      } = _ref4;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("AchievementItemCard.successfullyDeleted", {
            name: item.summary
          }),
          timeout: 10000
        }));
        setItemState(undefined);
        mixpanel.track(`Insights Highlights – Delete`, {
          lineItemid: item.id
        });
      }
    }));
  };
  const handleTopicClick = topicId => {
    const url = `${window.location.origin}${RoutesEnum.TOPIC.replace(":topicId", topicId)}?view=footprints`;
    window.open(url, "_blank");
    mixpanel.track(`Insights Highlights – Click Topic`, {
      topicId
    });
  };
  return /*#__PURE__*/React.createElement(Card, rest, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), itemState.isDismissed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      px: 3,
      py: 0.5,
      mb: 3
    }
  }, t("AchievementItemCard.dismissed")), /*#__PURE__*/React.createElement(Markdown, {
    s: {
      mb: 3,
      pr: 6
    },
    style: {
      flexGrow: 1
    },
    fontSize: 4,
    lineHeight: 6,
    text: itemState.summary
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto"
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, itemState.topics.map(topic => /*#__PURE__*/React.createElement(StyledTopicButton, {
    key: topic.id,
    onClick: () => handleTopicClick(topic.id),
    fontSize: 3,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": topic.name,
    s: {
      px: 2,
      py: 1
    },
    maxWidth: "280px",
    color: "translucentPrimary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "timelapse",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), topic.name))), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Dropdown, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.options"),
    display: "inline-block",
    direction: "bottomLeft",
    handlerProps: {
      color: "transparent",
      rounded: true,
      s: {
        p: 2.5
      }
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      display: "block",
      src: "dots-vertical"
    })
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        onItemClick();
        handleDelete();
      }
    }, deleteStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      scale: 4,
      color: "primary",
      s: {
        ml: 1
      }
    })));
  })), /*#__PURE__*/React.createElement(StyledBox, {
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": itemState.isDismissed ? t("common.actions.restore") : t("common.actions.dismiss"),
    onClick: () => handleToggleDismiss(),
    color: "transparent",
    rounded: true,
    outline: false,
    loading: dismissStatus === StatusStateEnum.LOADING,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: itemState.isDismissed ? "refresh" : "cross",
    color: "text",
    display: "block"
  }))));
};
export default AchievementItemCard;